"use server";

import { cookies } from "next/headers";
import { revalidatePath } from "next/cache";
import {
  COOKIE_WINDOWS,
  isWindowsLayoutState,
  type WindowsLayoutState,
} from "./types";
import { readWindowsCookie } from "./cookies";
import { apiClient, ApiClientError } from "@/lib/api/client";
import { getImperialSession } from "@/lib/api/session";
import { revalidateCmsPageCache } from "@/lib/admin/cache";

/**
 * Server Action — bulk-replace windows w pojedynczym obszarze przez backend.
 *
 * **Backend SoT pattern:** zastępuje dotychczasowy zapis do cookie. Po zmianie
 * w panelu admin (drag/drop) React wysyła pełną listę `windowIds` dla obszaru,
 * backend atomicznie DELETE + INSERT do `imp_cms_catalog_window`.
 *
 * **Type-discrimination** zgodne z cascade:
 *   - homepage -> type=0, idCatalog=null
 *   - catalog page -> type=1, idCatalog=X
 *   - article (TODO Faza F+) -> type=3, idCatalog=X, requestUri=...
 *
 * **Auth:** wymaga Bearer token z SSO (`getImperialSession`). Bez sesji
 * fail-soft -> zwracamy `{ ok: false, error: 'unauthenticated' }` żeby
 * UI mogło pokazać toast bez crash'u.
 */
export async function saveAreaWindowsAction(input: {
  area: string;
  windowIds: number[];
  type: 0 | 1 | 2 | 3;
  idCatalog: number | null;
  requestUri: string | null;
  /** Locale ISO ('pl', 'en', 'de') — backend mapuje na id_domain_language. */
  lang: string;
  /** Slug do revalidatePath po sukcesie. */
  slug: string;
}): Promise<{ ok: boolean; error?: string; inserted?: number }> {
  const session = await getImperialSession();
  if (!session) {
    return { ok: false, error: "unauthenticated" };
  }

  try {
    const body = {
      area: input.area,
      type: input.type,
      id_catalog: input.idCatalog,
      request_uri: input.requestUri,
      window_ids: input.windowIds,
    };
    const response = await apiClient<{
      data: { inserted: number; window_ids: number[] };
      meta: { area: string };
    }>("/IRESTAPI/v1/IWINDOWS_MANAGER_CONTROLLER/saveAreaWindows", {
      method: "POST",
      bearerToken: session.accessToken,
      lang: input.lang,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    await revalidateCmsPageCache(input.slug);
    return { ok: true, inserted: response.data.inserted };
  } catch (e) {
    if (e instanceof ApiClientError) {
      return { ok: false, error: e.message };
    }
    return { ok: false, error: "unexpected_error" };
  }
}

/**
 * Server Action — pełne usunięcie okna (`imp_cms_window`).
 *
 * **Backend:** `DELETE /IRESTAPI/v1/IWINDOWS_MANAGER_CONTROLLER/deleteWindow/{id}`
 * (handler `IAPI_deleteWindow`, OpenAPI path `/cms/windows/{id}`). Cascade FK auto-czyści przypisania w
 * `imp_cms_catalog_window` (homepage, catalog pages, articles — wszystkie
 * typy/id_catalog/request_uri jednocześnie).
 *
 * **Walidacja per-domain:** backend sprawdza że okno należy do bieżącej
 * (domena, język) — cross-domain delete attempts blokowane.
 *
 * **Auth:** wymaga Bearer + scope `cms:manage_windows`.
 *
 * @param input.id ID okna z `imp_cms_window`
 * @param input.lang Locale ISO ('pl', 'en', 'de')
 * @param input.slug Slug aktualnej strony do `revalidatePath` po sukcesie
 */
export async function deleteWindowAction(input: {
  id: number;
  lang: string;
  slug: string;
}): Promise<{ ok: boolean; error?: string; deletedId?: number; name?: string }> {
  const session = await getImperialSession();
  if (!session) {
    return { ok: false, error: "unauthenticated" };
  }

  try {
    const response = await apiClient<{
      data: { deleted_id: number; name: string; success: boolean };
      meta: { id_domain_language: number; lang_symbol: string };
    }>(`/IRESTAPI/v1/IWINDOWS_MANAGER_CONTROLLER/deleteWindow/${input.id}`, {
      method: "DELETE",
      bearerToken: session.accessToken,
      lang: input.lang,
    });

    await revalidateCmsPageCache(input.slug);
    return {
      ok: true,
      deletedId: response.data.deleted_id,
      name: response.data.name,
    };
  } catch (e) {
    if (e instanceof ApiClientError) {
      console.warn(`[deleteWindowAction] failed: ${e.message}`);
      return { ok: false, error: e.message };
    }
    return { ok: false, error: "unexpected_error" };
  }
}

/**
 * Server Action — zapisuje windows state dla konkretnego slug'a do cookie map.
 *
 * **DEPRECATED — backend SoT.** Kept tymczasowo żeby AdminContext mutators
 * (`addWindowToArea`, `moveWindowInArea`, ...) nie crash'owały podczas
 * migracji na backend. Po pełnej integracji `saveAreaWindowsAction` z
 * AdminContext'em (z resolve'em slug -> id_catalog dla katalog'ów) ta funkcja
 * + cały cookie windows state zostaną usunięte.
 *
 * @deprecated Use `saveAreaWindowsAction` instead.
 */
export async function setWindowsForSlugAction(
  slug: string,
  state: WindowsLayoutState,
): Promise<{ ok: boolean; error?: string }> {
  if (!isWindowsLayoutState(state)) {
    return { ok: false, error: "Invalid WindowsLayoutState shape" };
  }
  if (typeof slug !== "string" || slug.length === 0) {
    return { ok: false, error: `Invalid slug: ${slug}` };
  }

  const existing = await readWindowsCookie();
  const next = { ...existing, [slug]: state };

  const cookieStore = await cookies();
  cookieStore.set(COOKIE_WINDOWS, JSON.stringify(next), {
    path: "/",
    maxAge: 60 * 60 * 24 * 365,
    sameSite: "lax",
    httpOnly: false,
  });

  revalidatePath(slug, "layout");
  return { ok: true };
}
